import { useState } from 'react';
import type { ListViewSetCollapse } from '../listview/types';

/* Whether each set on screen is open or closed. Every set opens and closes on
 * its own; the toolbar's "show resources in sets" is what does all of them at
 * once.
 *
 * What is tracked is which sets the user flipped away from what that toggle
 * asks for, not their absolute state: a set the user has not touched follows
 * the toggle, and so does a set that only turns up later — after a refresh, or
 * after the date range changed. Flipping the toggle drops the exceptions,
 * which is what makes it the expand-all/collapse-all it reads as.
 *
 * One state for both view modes: a set the user closed in the table is still
 * closed after switching to the cards.
 */
export default function useListViewSetCollapse(
  showChildren: boolean
): ListViewSetCollapse {
  /* The exceptions are stored together with the toggle they were made
     against. Comparing it here, rather than resetting from an effect, puts the
     reset in the same render as the toggle that caused it. */
  const [flipped, setFlipped] = useState<{
    showChildren: boolean;
    setIDs: string[];
  }>({ showChildren, setIDs: [] });
  const flippedSetIDs = flipped.showChildren === showChildren ? flipped.setIDs : [];
  return {
    isSetExpanded: (bookingID: string) =>
      flippedSetIDs.includes(bookingID) ? !showChildren : showChildren,
    onToggleSet: (bookingID: string) =>
      setFlipped({
        showChildren,
        setIDs: flippedSetIDs.includes(bookingID)
          ? flippedSetIDs.filter((setID) => setID !== bookingID)
          : [...flippedSetIDs, bookingID]
      })
  };
}
